import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { generateCurrentWeekPosts, BlogPost } from '@/services/blogGenerator';
import { SEO } from '@/components/SEO';

// Lightweight markdown → JSX renderer for blogGenerator's `# / ## / - / 1. / **bold**`
// output — no markdown dependency exists in this project yet, and the content
// shape is simple enough not to warrant adding one.
function renderMarkdown(md: string) {
  const blocks = md.trim().split(/\n\s*\n/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    if (lines[0].startsWith('## ')) {
      return (
        <h2 key={i} className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#567D38' }}>
          {lines[0].slice(3)}
        </h2>
      );
    }
    if (lines[0].startsWith('# ')) {
      return null; // title rendered separately as the page H1
    }
    if (lines.every(l => /^\d+\.\s/.test(l.trim()) || !l.trim())) {
      return (
        <ol key={i} className="list-decimal pl-6 space-y-2 my-4">
          {lines.filter(Boolean).map((l, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inlineBold(l.replace(/^\d+\.\s/, '')) }} />
          ))}
        </ol>
      );
    }
    if (lines.every(l => l.trim().startsWith('- ') || !l.trim())) {
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 my-4">
          {lines.filter(Boolean).map((l, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inlineBold(l.replace(/^- /, '')) }} />
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: inlineBold(block) }} />
    );
  });
}

function inlineBold(text: string) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    setPosts(generateCurrentWeekPosts());
  }, []);

  const post = useMemo(() => posts.find(p => p.slug === slug), [posts, slug]);
  const titleLine = post?.content.split('\n').find(l => l.startsWith('# '));
  const title = titleLine ? titleLine.slice(2) : post?.title;
  const pullQuote = post?.excerpt;

  if (posts.length > 0 && !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-24 text-center" style={{ background: '#FAF7F2' }}>
        <h1 className="font-serif text-3xl mb-4" style={{ color: '#1A2E12' }}>Article not found</h1>
        <p className="mb-6" style={{ color: '#6B7B6E' }}>This story may have moved or expired.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 font-semibold" style={{ color: '#567D38' }}>
          <ArrowLeft className="w-4 h-4" /> Back to the Journal
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF7F2' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#D9B38C', borderTopColor: '#567D38' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F2' }}>
      {post.metaDescription && (
        <SEO
          title={`${post.title} | Marple & Co Journal`}
          description={post.metaDescription}
          path={`/blog/${post.slug}`}
        />
      )}

      <article className="max-w-2xl mx-auto px-6 py-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline"
          style={{ color: '#567D38' }}
        >
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>

        <div className="flex items-center gap-2 mb-4">
          <span
            className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(189,214,48,.15)', color: '#567D38' }}
          >
            {post.category}
          </span>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-6" style={{ color: '#1A2E12' }}>
          {title}
        </h1>

        <div className="flex items-center gap-5 text-sm mb-10" style={{ color: '#6B7B6E' }}>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {new Date(post.publishDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {post.readingTime} min read
          </span>
        </div>

        {pullQuote && (
          <blockquote
            className="font-serif italic text-2xl leading-snug my-10 pl-6"
            style={{ color: '#567D38', borderLeft: '3px solid #BDD630' }}
          >
            {pullQuote}
          </blockquote>
        )}

        <div className="font-serif text-lg" style={{ color: '#1A2E12' }}>
          {renderMarkdown(post.content)}
        </div>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t" style={{ borderColor: '#D9B38C40' }}>
            {post.tags.map(tag => (
              <span
                key={tag}
                className="text-xs px-3 py-1 rounded-full"
                style={{ background: '#F7F5EE', color: '#6B7B6E' }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}

export default Article;
